import { Decimal, toIstanbulDate } from "@station/core";
import { z } from "zod";
import { defaultUnitPriceScale, lineGross, saleLineKindSchema, } from "./types.js";
/**
 * Abonelik hakkı (saf) — ADR 0017 §27, `pos-local-api.md` §7.2 kural 4.
 *
 * Abonelik kapsadığı satırı SIFIRLAR (gelir abonelik satışında yazılmıştır): hak kullanımı `campaignId: null`, gerekçe
 * `ABONELIK:<planKodu>` olan tam tutarlı bir indirim satırıdır ve satır kampanyaya KAPANIR (bir satıra tek indirim).
 *
 *  - Kapsam: satırın ürünü `coveredProductIds`'te YA DA türü `coveredLineKinds`'ta (ikisi de boşsa hiçbir şey kapsanmaz).
 *  - Hak BÜTÜN satırı karşılamalıdır (`quantity ≤ kalan` ve günlük sınır); kısmi karşılama yok (yıkamada adet 1'dir; şarjda
 *    kWh kotası Prompt 19'da genişletilebilir).
 *  - Dönem sonu İstanbul günüdür ve DAHİLDİR (`gün ≤ periodEnd`). Günlük kullanım anlık görüntüsü başka güne aitse sıfırdır.
 *  - Araca bağlı abonelik yalnızca aynı araçla kullanılır.
 */
const quantitySchema = z.string().regex(/^\d{1,15}(\.\d{1,3})?$/, "Miktar, en çok 3 ondalık");
export const subscriptionEntitlementSchema = z.object({
    subscriptionId: z.uuid(),
    planCode: z.string().min(1).max(40),
    planName: z.string().max(120).optional(),
    coveredProductIds: z.array(z.uuid()).max(200).default([]),
    coveredLineKinds: z.array(saleLineKindSchema).max(5).default([]),
    /** Dönemin son günü (dahil), `YYYY-MM-DD`. */
    periodEnd: z.iso.date(),
    remainingQuantity: quantitySchema,
    dailyLimit: quantitySchema.nullable().default(null),
    /** `usedToday`'in ait olduğu gün. */
    usageDay: z.iso.date(),
    usedToday: quantitySchema,
    vehicleId: z.uuid().nullable().default(null),
});
export const ENTITLEMENT_REASONS = [
    "NOT_COVERED",
    "EXPIRED",
    "VEHICLE_MISMATCH",
    "EXHAUSTED",
    "DAILY_LIMIT",
];
export const ENTITLEMENT_REASON_LABELS = {
    NOT_COVERED: "Abonelik bu ürünü kapsamıyor",
    EXPIRED: "Abonelik dönemi bitmiş",
    VEHICLE_MISMATCH: "Abonelik başka bir araca bağlı",
    EXHAUSTED: "Dönem hakkı bitti",
    DAILY_LIMIT: "Günlük kullanım sınırı doldu",
};
export function entitlementCovers(entitlement, line) {
    return (entitlement.coveredProductIds.includes(line.productId) || entitlement.coveredLineKinds.includes(line.kind));
}
/** Tek satırın hak denetimi (sıra: kapsam → dönem → araç → dönem hakkı → günlük sınır). */
export function checkEntitlement(entitlement, request) {
    const at = typeof request.at === "string" ? new Date(request.at) : request.at;
    const day = toIstanbulDate(at);
    const quantity = Decimal.from(request.quantity);
    const remaining = Decimal.from(entitlement.remainingQuantity);
    const usedToday = Decimal.from(entitlement.usageDay === day ? entitlement.usedToday : "0");
    const dailyRemaining = entitlement.dailyLimit === null
        ? null
        : Decimal.max(Decimal.ZERO, Decimal.from(entitlement.dailyLimit).sub(usedToday));
    const unchanged = (reason) => ({
        ok: false,
        reason,
        remainingAfter: remaining.toFixed(3),
        dailyRemainingAfter: dailyRemaining === null ? null : dailyRemaining.toFixed(3),
    });
    if (!entitlementCovers(entitlement, request))
        return unchanged("NOT_COVERED");
    if (day > entitlement.periodEnd)
        return unchanged("EXPIRED");
    if (entitlement.vehicleId !== null && entitlement.vehicleId !== (request.vehicleId ?? null))
        return unchanged("VEHICLE_MISMATCH");
    if (!quantity.isPositive() || quantity.gt(remaining))
        return unchanged("EXHAUSTED");
    if (dailyRemaining !== null && quantity.gt(dailyRemaining))
        return unchanged("DAILY_LIMIT");
    return {
        ok: true,
        reason: null,
        remainingAfter: remaining.sub(quantity).toFixed(3),
        dailyRemainingAfter: dailyRemaining === null ? null : dailyRemaining.sub(quantity).toFixed(3),
    };
}
/** Kullanımdan sonraki anlık görüntü (ajanın yerel düşümü için). */
export function consumeEntitlement(entitlement, quantity, at) {
    const day = toIstanbulDate(typeof at === "string" ? new Date(at) : at);
    const usedToday = Decimal.from(entitlement.usageDay === day ? entitlement.usedToday : "0");
    return {
        ...entitlement,
        remainingQuantity: Decimal.from(entitlement.remainingQuantity).sub(quantity).toFixed(3),
        usageDay: day,
        usedToday: usedToday.add(quantity).toFixed(3),
    };
}
/**
 * Sepete hakları uygular: her satır, onu tam karşılayan İLK aboneliği (dönem sonu yakın olan önce, sonra kimlik) alır;
 * hak satır satır tüketilir (aynı üründen iki satır ikinci satırda hakkı bitmiş bulabilir). İndirim = satır brütü.
 * POS atanan satırları `campaignsDisabled` yapar ve indirimleri `computeBasket`'e `manualDiscounts` olarak verir.
 */
export function applyEntitlements(lines, entitlements, context) {
    const pool = [...entitlements].sort((a, b) => (a.periodEnd < b.periodEnd ? -1 : a.periodEnd > b.periodEnd ? 1 : 0) ||
        (a.subscriptionId < b.subscriptionId ? -1 : a.subscriptionId > b.subscriptionId ? 1 : 0));
    const discounts = [];
    const assignments = [];
    for (const line of lines) {
        const gross = lineGross(line.unitPrice, line.quantity, line.unitPriceScale ?? defaultUnitPriceScale(line.kind));
        if (!gross.isPositive())
            continue;
        const index = pool.findIndex((entitlement) => checkEntitlement(entitlement, {
            productId: line.productId,
            kind: line.kind,
            quantity: line.quantity,
            at: context.at,
            vehicleId: context.vehicleId ?? null,
        }).ok);
        if (index < 0)
            continue;
        const entitlement = pool[index];
        pool[index] = consumeEntitlement(entitlement, line.quantity, context.at);
        discounts.push({
            lineNo: line.lineNo,
            campaignId: null,
            amount: gross.toString(),
            reason: `ABONELIK:${entitlement.planCode}`,
        });
        assignments.push({ lineNo: line.lineNo, subscriptionId: entitlement.subscriptionId });
    }
    return { discounts, assignments };
}
//# sourceMappingURL=entitlement.js.map